import { useUserContext } from "../../../../contexts/UserContext";
import { IOption, LandingPageOptions } from "../../../../types";
import {
  getGroupIdOptionBySetupAndStore,
  getGroupIdOptionsBySetup,
  getStoreOptionByGroupId,
  getStoreOptionsFromExtendedAccountInfo,
} from "../../helpers";
import {
  DEFAULT_OPTION,
  GL_SETUPS,
  LOADING_DEFAULT_IOPTION,
  NO_RESULTS_DEFAULT_IOPTION,
} from "../../constants";

interface UseDynamicIdSelectorParams {
  handleLandingPageOptionsUpdate: (
    landingPageOptions: LandingPageOptions,
  ) => void;
}

export const useDynamicIdSelector = ({
  handleLandingPageOptionsUpdate,
}: UseDynamicIdSelectorParams) => {
  const {
    defaultStore,
    selected,
    setSelected,
    iamUserState,
    user,
    setupListNavInfoByDepartment,
    resolvedLaunchContext,
  } = useUserContext();

  const { setup, department, departmentId, store } = selected;

  const groups = iamUserState?.eisGroups?.groups;
  const extendedAccountInfo = iamUserState?.extendedAccountInfo;

  const departmentIds =
    setupListNavInfoByDepartment[department.value]?.departmentIds;

  const stores = getStoreOptionsFromExtendedAccountInfo(
    extendedAccountInfo ?? [],
  );

  const getGroupOptions = (): IOption[] => {
    if (!groups) {
      return [];
    }

    if (resolvedLaunchContext.launchMode === "payments") {
      const groupOption = getGroupIdOptionBySetupAndStore({
        groupIds: groups,
        setup: setup.value,
        store: defaultStore.value,
      });

      if (!groupOption.value) {
        return [];
      }

      return [groupOption];
    }

    return getGroupIdOptionsBySetup({
      groupIds: groups,
      setup: setup.value,
    });
  };

  const getComputedGroupOptions = (): IOption[] => {
    const groupOptions = getGroupOptions();

    if (groupOptions.length === 0) {
      return [NO_RESULTS_DEFAULT_IOPTION()];
    }

    return groupOptions;
  };

  const getComputedStoreOptions = (): IOption[] => {
    if (!extendedAccountInfo) {
      return [LOADING_DEFAULT_IOPTION()];
    }

    if (stores.length === 0) {
      return [NO_RESULTS_DEFAULT_IOPTION()];
    }

    return stores;
  };

  const getComputedDepartmentIdOptions = (): IOption[] => {
    if (!departmentIds) {
      return [LOADING_DEFAULT_IOPTION()];
    }

    if (departmentIds.length === 0) {
      return [NO_RESULTS_DEFAULT_IOPTION()];
    }

    return departmentIds.map(({ name, departmentId }) => ({
      label: `${departmentId} - ${name}`,
      value: departmentId,
    }));
  };

  const computedGroupOptions = getComputedGroupOptions();
  const computedStoreOptions = getComputedStoreOptions();
  const computedDepartmentIdsOptions = getComputedDepartmentIdOptions();

  const shouldShowCompanyId = GL_SETUPS.includes(setup.value);

  const handleClearGroupDropdown = () => {
    setSelected((previous) => ({
      ...previous,
      groupId: DEFAULT_OPTION,
      store: DEFAULT_OPTION,
    }));
  };

  const handleClearDropdown = () => {
    setSelected((previous) => ({
      ...previous,
      store: DEFAULT_OPTION,
    }));
  };

  const handleSelectedDepartmentId = (option: IOption) => {
    const targetStoreOfDepartmentId = departmentIds?.find(
      ({ departmentId }) => departmentId === option.value,
    );

    const storeOption = computedStoreOptions.find(
      ({ value }) => value === targetStoreOfDepartmentId?.storeId,
    );

    const updatedLandingPageOptions: LandingPageOptions = {
      ...selected,
      store: storeOption || DEFAULT_OPTION,
      departmentId: option,
    };

    setSelected(updatedLandingPageOptions);
    handleLandingPageOptionsUpdate(updatedLandingPageOptions);
  };

  const handleSelectedGroupId = (option: IOption) => {
    const selectedStoreOption = getStoreOptionByGroupId({
      selectedGroupId: option.value,
      iamUserState,
    });

    const updatedLandingPageOptions: LandingPageOptions = {
      ...selected,
      groupId: option,
      store: selectedStoreOption,
    };

    setSelected(updatedLandingPageOptions);
    handleLandingPageOptionsUpdate(updatedLandingPageOptions);
  };

  const handleSelectedStore = (option: IOption) => {
    const updatedLandingPageOptions: LandingPageOptions = {
      ...selected,
      store: option,
    };

    setSelected((previous) => ({
      ...previous,
      store: option,
    }));

    handleLandingPageOptionsUpdate(updatedLandingPageOptions);
  };

  return {
    selected,
    user,
    department,
    departmentId,
    store,

    shouldShowCompanyId,

    computedGroupOptions,
    computedStoreOptions,
    computedDepartmentIdsOptions,

    handleClearGroupDropdown,
    handleClearDropdown,
    handleSelectedDepartmentId,
    handleSelectedGroupId,
    handleSelectedStore,
  };
};
